import React, { useEffect, useState } from 'react'
import { TextField, Button, Typography, CircularProgress, Box } from '@material-ui/core'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_PROJECT_PROMPT,
  CREATE_PROJECT_PROMPT,
  UPDATE_PROJECT_PROMPT,
} from '../../../entities/prompts/queries'

function ProjectPromptEditor({ project, onSuccess, onError }) {
  const { data, loading, refetch } = useQuery(GET_PROJECT_PROMPT, {
    variables: { projectId: project._id },
  })

  const [promptContent, setPromptContent] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  const [createProjectPrompt, { loading: isCreating }] = useMutation(CREATE_PROJECT_PROMPT, {
    onCompleted: () => {
      refetch()
      setIsDirty(false)
      onSuccess && onSuccess(`Project prompt created successfully for ${project.name}`)
    },
    onError: error => {
      onError && onError(`Error creating project prompt: ${error.message}`)
    },
    refetchQueries: [{ query: GET_PROJECT_PROMPT, variables: { projectId: project._id } }],
  })

  const [updateProjectPrompt, { loading: isUpdating }] = useMutation(UPDATE_PROJECT_PROMPT, {
    onCompleted: () => {
      refetch()
      setIsDirty(false)
      onSuccess && onSuccess(`Project prompt updated successfully for ${project.name}`)
    },
    onError: error => {
      onError && onError(`Error updating project prompt: ${error.message}`)
    },
  })

  useEffect(() => {
    if (!loading) {
      setPromptContent(data?.getProjectPrompt?.content || '')
      setIsDirty(false)
    }
  }, [loading, data])

  const handleContentChange = e => {
    setPromptContent(e.target.value)
    setIsDirty(e.target.value !== (data?.getProjectPrompt?.content || ''))
  }

  const handleSave = () => {
    const promptExists = !!data?.getProjectPrompt
    if (promptExists) {
      updateProjectPrompt({
        variables: {
          data: {
            projectId: project._id,
            content: promptContent,
            expectedVersion: data.getProjectPrompt.version,
          },
        },
      })
    } else {
      createProjectPrompt({
        variables: {
          data: {
            name: `${project.name} Prompt`,
            content: promptContent,
            projectId: project._id,
          },
        },
      })
    }
  }

  if (loading) {
    return <CircularProgress />
  }

  const isLoading = isCreating || isUpdating

  return (
    <Box display="flex" flexDirection="column" gridGap="16px" width="100%">
      <Typography variant="body2">
        Add a specific prompt for this project. If a specific prompt is added, the system prompt
        will still be used.
      </Typography>
      <TextField
        label={`${project.name} Prompt`}
        multiline
        minRows={8}
        maxRows={20}
        value={promptContent}
        onChange={handleContentChange}
        variant="outlined"
        fullWidth
      />
      <Box alignSelf="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading || !isDirty}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  )
}

export default ProjectPromptEditor
